import { useMemo, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { NormalizedTask } from '../../domain/normalize';
import {
  computeKpis, sumBy, countBy, serieMensual, areaPorDificultadMetric,
  metricValue, metricLabel,
} from '../../domain/metrics';
import { UserDoc } from '../../domain/types';
import {
  Card, Kpi, BarCard, BoardIntro, MetricToggle, MetricKey,
  tooltipStyle, axisTick, GRID, CHART_CURSOR, pct,
} from './shared';

export default function BoardGeneral({ tasks }: { tasks: NormalizedTask[]; users: UserDoc[] }) {
  const [trendMetric, setTrendMetric] = useState<MetricKey>('tareas');
  const [solicitanteMetric, setSolicitanteMetric] = useState<MetricKey>('horas');
  const [areaMetric, setAreaMetric] = useState<MetricKey>('horas');

  const k = useMemo(() => computeKpis(tasks), [tasks]);
  const serie = useMemo(() => serieMensual(tasks), [tasks]);
  const axd = useMemo(() => areaPorDificultadMetric(tasks, areaMetric), [tasks, areaMetric]);

  const porSolicitante = useMemo(
    () => sumBy(tasks.filter((t) => !t.excluido), (t) => t.solicitante, (t) => metricValue(t, solicitanteMetric)),
    [tasks, solicitanteMetric]
  );

  // Resumen por solicitante (tabla con % horas y % tareas)
  const resumen = useMemo(() => {
    const horas = sumBy(tasks.filter((t) => !t.excluido), (t) => t.solicitante, (t) => t.horas_totales);
    const qty = countBy(tasks.filter((t) => !t.excluido), (t) => t.solicitante);
    const totH = horas.reduce((s, x) => s + x.value, 0);
    const totQ = qty.reduce((s, x) => s + x.value, 0);
    const qmap = new Map(qty.map((x) => [x.name, x.value]));
    return horas.map((h) => ({ name: h.name, horas: h.value, pctH: pct(h.value, totH), tareas: qmap.get(h.name) || 0, pctQ: pct(qmap.get(h.name) || 0, totQ) }));
  }, [tasks]);

  return (
    <div className="grid">
      <BoardIntro
        titulo="Resumen general"
        objetivo="Una foto rápida de todo el trabajo del período: cuánto se pidió, cuántas horas llevó, qué áreas piden más y cuánto se reprocesa."
        preguntas={['¿Cuánto trabajo hubo este mes?', '¿Quién pide más?', '¿Cuánto del esfuerzo se va en rehacer cosas?']}
      />
      {/* Fila 1 — volumen */}
      <div className="cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px,1fr))' }}>
        <Kpi label="Horas reales" value={String(k.horasReales)} help="Suma de horas reales trabajadas cargadas en las tareas. No incluye estimadas." />
        <Kpi label="Pedidos / tareas" value={String(k.qtyTareas)} help="Cantidad de pedidos del período. No cuenta tareas canceladas ni rechazadas." />
        <Kpi label="% urgentes" value={`${k.pctUrgentes}%`} tone={k.pctUrgentes > 40 ? 'bad' : undefined} help="Tareas con urgencia alta o marcadas como extraordinarias, sobre el total." />
      </div>

      {/* Fila 2 — reproceso y cumplimiento */}
      <div className="cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px,1fr))' }}>
        <Kpi label="Con retrabajo" value={String(k.qtyConRetrabajo)} sub={`${k.pctConRetrabajo}% del total`} tone={k.pctConRetrabajo > 30 ? 'warn' : undefined} help="Tareas que se reabrieron al menos una vez (un admin las pasó de finalizada a activa). Reproceso a pedido interno." />
        <Kpi label="Ajustes de cliente" value={String(k.qtyAjustes)} sub={`${k.pctAjustes}% del total`} help="Tareas que son un ajuste pedido por el cliente (vinculadas a una tarea anterior por el campo ajuste)." />
        <Kpi label="% a tiempo" value={`${k.pctATiempo}%`} tone={k.pctATiempo >= 80 ? 'ok' : k.pctATiempo < 60 ? 'bad' : 'warn'} help="De las tareas ya entregadas, cuántas llegaron en fecha o antes." />
        <Kpi label="Demora prom. (días)" value={k.demoraProm != null ? String(k.demoraProm) : '—'} help="Días promedio desde que se pide una tarea hasta que se entrega." />
      </div>

      <Card
        title={`Evolutivo mensual (${metricLabel(trendMetric).toLowerCase()})`}
        hint="Una sola unidad por eje: tareas o horas por mes de solicitud."
        action={<MetricToggle value={trendMetric} onChange={setTrendMetric} />}
      >
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={serie} margin={{ left: -10, right: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
            <XAxis dataKey="mes" tick={axisTick} />
            <YAxis allowDecimals={trendMetric === 'horas'} tick={axisTick} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: CHART_CURSOR }} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey={trendMetric} name={metricLabel(trendMetric)} stroke={trendMetric === 'horas' ? '#15803d' : '#4f46e5'} strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <div className="chart-pair">
        <BarCard
          title={`Por espacio / cliente interno (${metricLabel(solicitanteMetric).toLowerCase()})`}
          data={porSolicitante}
          color="#4f46e5"
          horizontal
          height={Math.max(440, porSolicitante.length * 34 + 60)}
          tickFontSize={13}
          yAxisWidth={170}
          allowDecimals={solicitanteMetric === 'horas'}
          action={<MetricToggle value={solicitanteMetric} onChange={setSolicitanteMetric} />}
        />
        <Card
          title={`Por área de producción (${metricLabel(areaMetric).toLowerCase()})`}
          hint="Barras apiladas por nivel de dificultad; la unidad del eje cambia con el selector."
          action={<MetricToggle value={areaMetric} onChange={setAreaMetric} />}
        >
          <ResponsiveContainer width="100%" height={440}>
            <BarChart data={axd} margin={{ left: -10, right: 10, bottom: 40 }} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 13, fill: 'var(--text-tertiary)' }} angle={-25} textAnchor="end" interval={0} height={60} />
              <YAxis allowDecimals={areaMetric === 'horas'} tick={{ fontSize: 13, fill: 'var(--text-tertiary)' }} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: CHART_CURSOR }} /><Legend wrapperStyle={{ fontSize: 13 }} />
              <Bar dataKey="alta" name="Alta" stackId="d" fill="#b91c1c" maxBarSize={64} />
              <Bar dataKey="media" name="Media" stackId="d" fill="#f59e0b" maxBarSize={64} />
              <Bar dataKey="baja" name="Baja" stackId="d" fill="#16a34a" maxBarSize={64} />
              <Bar dataKey="sin" name="Sin dato" stackId="d" fill="#cbd5e1" maxBarSize={64} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card title="Resumen por espacio / cliente interno">
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead><tr><th>Solicitante</th><th className="num">Horas</th><th className="num">% horas</th><th className="num">Tareas</th><th className="num">% tareas</th></tr></thead>
            <tbody>
              {resumen.map((r) => (
                <tr key={r.name}><td>{r.name}</td><td className="num">{r.horas}</td><td className="num">{r.pctH}</td><td className="num">{r.tareas}</td><td className="num">{r.pctQ}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
